import { CircularProgress, Button as MuiButton, ButtonProps as MuiButtonProps } from '@material-ui/core';
import cx from 'classnames';
import { HTMLProps, useMemo } from 'react';

import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { Tooltip, TooltipDelay, TooltipProps } from '@shared/components/tooltip';
import { useConfigAll } from '@shared/contexts/ConfigContext';
import { ButtonColour, ButtonSize, NewButtonColour } from '@shared/types/common/button';
import { getButtonColour } from '@shared/utils/common';

import { styles } from './button.styles';

const getButtonColourFromLegacy = (colour: ButtonColour): NewButtonColour => {
  switch (colour) {
    case ButtonColour.secondaryGhost:
      return NewButtonColour.Ghost;

    case ButtonColour.secondary:
    case ButtonColour.secondaryColour:
      return NewButtonColour.Outline;

    case ButtonColour.primary:
      return NewButtonColour.PrimaryNavy;
  }
};

export interface ButtonProps extends Omit<MuiButtonProps, 'classes' | 'color' | 'size'>, WithStyles<typeof styles> {
  active?: boolean;
  arrow?: boolean;
  colour?: ButtonColour | NewButtonColour;
  component?: React.ElementType;
  disabled?: boolean;
  hovered?: boolean;
  loading?: boolean;
  size?: ButtonSize;
  text?: React.ReactNode;
  tooltip?: string;
  tooltipDelay?: TooltipDelay;
  tooltipPlacement?: TooltipProps['placement'];
  target?: HTMLProps<HTMLAnchorElement>['target'];
}

const ButtonComponent: React.FC<ButtonProps> = ({
  active = false,
  arrow = true,
  children,
  classes,
  colour: suppliedColour,
  disabled = false,
  hovered = false,
  loading = false,
  size = ButtonSize.medium,
  text,
  tooltip,
  tooltipDelay,
  tooltipPlacement = 'left',
  ...buttonProps
}) => {
  const { appId } = useConfigAll();

  const colour: NewButtonColour = useMemo(() => {
    // Default
    if (typeof suppliedColour === 'undefined') {
      return NewButtonColour.Outline;
    }

    // Legacy
    if (!Object.values(NewButtonColour).includes(suppliedColour as NewButtonColour)) {
      return getButtonColourFromLegacy(suppliedColour as ButtonColour);
    }

    // New
    return getButtonColour(suppliedColour as NewButtonColour, appId);
  }, [appId, suppliedColour]);

  const buttonSpinner = useMemo(
    () => (
      <CircularProgress
        classes={{ root: classes.buttonSpinner }}
        color={colour === NewButtonColour.PrimaryNavy ? 'secondary' : 'primary'}
        size={size === ButtonSize.extraSmall ? 12 : 15}
        style={{ marginLeft: 8 }}
      />
    ),
    [classes.buttonSpinner, colour, size]
  );

  const startIcon = useMemo(() => {
    // Replace start icon with spinner when loading, if start icon is present
    if (loading && buttonProps.startIcon) {
      return buttonSpinner;
    }

    return buttonProps.startIcon;
  }, [buttonProps.startIcon, buttonSpinner, loading]);

  const endIcon = useMemo(() => {
    // Replace end icon with spinner when loading, if start icon is not present
    if (loading && !buttonProps.startIcon) {
      return buttonSpinner;
    }

    return buttonProps.endIcon;
  }, [buttonProps.endIcon, buttonProps.startIcon, buttonSpinner, loading]);

  const content = (
    <MuiButton
      {...buttonProps}
      classes={{
        root: cx(classes.root, classes[colour], classes[size], {
          [classes.buttonDisabled]: disabled,
          [classes.buttonActive]: active,
          [classes.buttonHovered]: hovered,
          [classes.buttonLoading]: loading,
        }),
        startIcon: classes.buttonStartIcon,
        label: classes.buttonLabel,
        endIcon: classes.buttonEndIcon,
      }}
      disabled={disabled}
      disableRipple
      endIcon={endIcon}
      startIcon={startIcon}
    >
      {text || children}
    </MuiButton>
  );

  if (tooltip) {
    return (
      <Tooltip
        arrow={arrow}
        delay={tooltipDelay ?? TooltipDelay.Delayed}
        placement={tooltipPlacement || undefined}
        title={tooltip}
      >
        {content}
      </Tooltip>
    );
  }

  return content;
};

export const Button = withStyles(styles)(ButtonComponent);
